// Verwaltung › Audit-Protokoll (Export, Siegel) und Datenschutz (Auskunft, Löschung nach Fristablauf).
import { CHANNELS, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'

const zTimestamp = z.string()
const AUDIT_CHANNELS = [...CHANNELS, 'pos-offline'] as const

export const AUDIT_CHANNEL_LABELS: Record<string, string> = {
  ui: 'Oberfläche',
  api: 'Schnittstelle (API)',
  assistant: 'KI-Assistent',
  'assistant-proactive': 'KI-Assistent (Hinweis)',
  import: 'Import',
  system: 'System',
  'pos-offline': 'Kasse (nachgetragen)',
}

/** Was sich im Audit-Protokoll filtern lässt: Personen, Aktionen, Arten von Datensätzen – mit deutschen Namen. */
export const auditFacets = defineContract({
  name: 'platform.audit.facets',
  title: 'Filter des Audit-Protokolls',
  description: 'Personen, Aktionen und Arten von Datensätzen, die im Audit-Protokoll vorkommen – für die Filter der Ansicht.',
  llm: { description: 'Liefert Filterwerte für die Ansicht des Audit-Protokolls. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({}),
  output: z.object({
    people: z.array(z.object({ userId: zId, displayName: z.string(), technical: z.boolean() })),
    actions: z.array(z.object({ name: z.string(), title: z.string(), module: z.string(), moduleTitle: z.string() })),
    entityTypes: z.array(z.object({ type: z.string(), label: z.string() })),
    channels: z.array(z.object({ value: z.string(), label: z.string() })),
    /** Standorte, an denen der Leser das Protokoll sehen darf */
    locations: z.array(z.object({ id: zId, label: z.string() })),
  }),
  permission: 'platform.audit.read',
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})

export const AUDIT_EXPORT_MAX_ROWS = 5000

export const auditExport = defineContract({
  name: 'platform.audit.export',
  title: 'Audit-Protokoll exportieren',
  description:
    'Erstellt aus dem gefilterten Ausschnitt des Audit-Protokolls eine CSV-Datei. Vertrauliche Einträge und Felder bleiben verborgen wie in der Ansicht.',
  llm: {
    description:
      'Exportiert einen gefilterten Zeitraum des Audit-Protokolls als CSV-Text (höchstens 5.000 Einträge). Verwenden, wenn ' +
      'ein Auszug für Prüfer oder Steuerberatung gebraucht wird. Zum Nachsehen einzelner Änderungen platform.audit.list.',
    keywords: ['Audit', 'Protokoll', 'Export', 'CSV', 'Prüfer', 'Auszug'],
  },
  input: z.strictObject({
    entityType: z.string().min(1).max(80).optional(),
    userId: zId.optional(),
    /** Zeitraum: from einschließlich, to ausschließlich (ISO 8601) */
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    channel: z.enum(AUDIT_CHANNELS).optional(),
    action: z.string().min(1).max(120).optional(),
    locationId: zId.optional(),
    q: z.string().trim().max(200).optional(),
  }),
  output: z.object({
    fileName: z.string(),
    /** CSV mit Semikolon und BOM (öffnet in Excel) */
    csv: z.string(),
    rows: z.int().nonnegative(),
    /** Einträge, die wegen fehlender Rechte nur als Platzhalter enthalten sind */
    restricted: z.int().nonnegative(),
    /** true: Es gab mehr Einträge als die Höchstzahl – Zeitraum eingrenzen */
    truncated: z.boolean(),
  }),
  permission: 'platform.audit.read',
  risk: 'read',
  scope: (input) => (input.locationId ? [input.locationId] : 'any'),
  assistant: 'opt-in',
})

export const auditSeal = defineContract({
  name: 'platform.audit.seal',
  title: 'Versiegelung des Audit-Protokolls prüfen',
  description:
    'Zeigt den Stand der Versiegelung (letzter versiegelter Block, noch offene Einträge) und rechnet auf Wunsch die Kette aller Siegel nach.',
  llm: {
    description:
      'Liefert den Stand der Versiegelung des Audit-Protokolls und prüft mit verify: true die Hash-Kette aller Blöcke. ' +
      'Verwenden für „Ist das Protokoll unverändert?“. Ändert nichts.',
    keywords: ['Siegel', 'Versiegelung', 'manipuliert', 'unverändert', 'Hash', 'Audit'],
  },
  input: z.strictObject({
    /** true: alle Blöcke nachrechnen (kann bei großen Protokollen einige Sekunden dauern) */
    verify: z.boolean().default(false),
  }),
  output: z.object({
    seals: z.int().nonnegative(),
    sealedEntries: z.int().nonnegative(),
    unsealedEntries: z.int().nonnegative(),
    lastSeal: z.object({ sealedAt: zTimestamp, entryCount: z.int(), hash: z.string() }).nullable(),
    /** nur mit verify: Ergebnis der Prüfung */
    verification: z.object({ ok: z.boolean(), problems: z.array(z.object({ kind: z.string(), message: z.string() })) }).nullable(),
  }),
  // Einträge ohne Standort (Rollen, Einstellungen) sieht nur, wer das Protokoll unbeschränkt liest – das Siegel
  // umfasst alle Einträge des Betriebs
  permission: 'platform.audit.read',
  risk: 'read',
  scope: 'any',
})

// ---- Datenschutz ----------------------------------------------------------------------------------------------------

export const zErasurePartner = z.object({
  id: zId,
  number: z.string(),
  /** Platzhaltername nach dem Anonymisieren bzw. der Name, solange noch nicht anonymisiert */
  name: z.string(),
  kind: z.string(),
  anonymizedAt: zTimestamp.nullable(),
  /** festgeschriebene Belege, die noch Personenbezug tragen */
  documentsWithTraces: z.int().nonnegative(),
  /** davon bereits außerhalb der Aufbewahrungsfrist (zehn volle Jahre nach Jahresende) */
  documentsErasable: z.int().nonnegative(),
  /** Stände der Stammdaten-Historie, die noch Personenbezug tragen können */
  historyRows: z.int().nonnegative(),
})

export const privacyPartners = defineContract({
  name: 'platform.privacy.partners',
  title: 'Datenschutz: Geschäftspartner mit Löschbedarf',
  description:
    'Anonymisierte Geschäftspartner, deren Spuren (Historie, alte Belege) noch Personenbezug tragen – mit dem, was jetzt entfernt werden kann und was wegen laufender Aufbewahrungsfrist bleiben muss.',
  llm: { description: 'Listet Geschäftspartner mit Löschbedarf. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({}),
  output: z.object({
    partners: z.array(zErasurePartner),
    /** Jahr, bis zu dem Belege (einschließlich) außerhalb der Aufbewahrungsfrist liegen */
    erasableThroughYear: z.int(),
  }),
  permission: 'platform.privacy.manage',
  risk: 'read',
  assistant: 'never',
})

export const privacyErasePartner = defineContract({
  name: 'platform.privacy.erasePartner',
  title: 'Spuren eines Geschäftspartners entfernen',
  description:
    'Entfernt den Personenbezug eines anonymisierten Geschäftspartners aus Stammdaten-Historie, Suchindex und Belegen, deren Aufbewahrungsfrist abgelaufen ist. Belege behalten Nummer, Datum, Beträge, Steuer und Positionen; jüngere Belege bleiben unberührt, bis ihre Frist endet.',
  llm: { description: 'Entfernt Spuren eines Geschäftspartners. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ partnerId: zId }),
  output: z.object({
    partnerId: zId,
    /** geschwärzte Stände der Stammdaten-Historie */
    historyRows: z.int().nonnegative(),
    /** Belege, deren Personenbezug entfernt wurde */
    documents: z.int().nonnegative(),
    /** Belege, die wegen laufender Frist ihren Personenbezug behalten */
    documentsKept: z.int().nonnegative(),
  }),
  permission: 'platform.privacy.manage',
  risk: 'destructive',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const privacyMemberReport = defineContract({
  name: 'platform.privacy.memberReport',
  title: 'Auskunft: gespeicherte Daten einer Person',
  description:
    'Stellt zusammen, was das System über ein Mitglied des Betriebs speichert: Konto, Rollen, Sitzungen, Sicherheitsprotokoll, Einträge im Audit-Protokoll, Aufgaben, Benachrichtigungen, Unterhaltungen mit dem Assistenten (nur die Anzahl).',
  llm: { description: 'Erstellt die Datenauskunft zu einer Person. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ membershipId: zId }),
  output: z.object({
    generatedAt: zTimestamp,
    fileName: z.string(),
    person: z.object({
      displayName: z.string(),
      email: z.string(),
      locale: z.string(),
      memberSince: zTimestamp,
      status: z.string(),
      lastLoginAt: zTimestamp.nullable(),
    }),
    sections: z.array(
      z.object({
        title: z.string(),
        /** was gespeichert wird und wozu, in einem Satz */
        purpose: z.string(),
        /** Aufbewahrung in Worten */
        retention: z.string(),
        count: z.int().nonnegative(),
        rows: z.array(z.record(z.string(), z.string())),
        /** true: Es gibt mehr Einträge, als die Auskunft aufführt */
        truncated: z.boolean(),
      }),
    ),
  }),
  permission: 'platform.privacy.manage',
  // lesend, aber sie führt Personendaten zusammen: Der Abruf selbst steht im Audit-Protokoll
  risk: 'write',
  effects: { securitySensitive: true },
  assistant: 'never',
})
